import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Paper, styled } from '@mui/material'
import { CSSProperties, PropsWithChildren, memo } from 'react'
import { DragHandle } from './DragHandle'
import { colorPalette } from '../../../style/colorPalette'

export type Props = PropsWithChildren<{
  id: string
  isDragOverlay?: boolean
  cardStyle?: CSSProperties
  showDragHandle?: boolean
}>

const Card = memo(
  ({
    id,
    isDragOverlay,
    cardStyle,
    children,
    showDragHandle = true,
  }: Props) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
      setActivatorNodeRef,
    } = useSortable({
      id,
    })

    const style = {
      transform: CSS.Transform.toString(transform),
      transition: transition,
      opacity: isDragging ? 0.5 : 1,
      cursor: isDragOverlay ? 'grabbing' : 'grab',
      ...cardStyle,
    }

    return (
      <CardPaper
        elevation={1}
        style={style}
        ref={setNodeRef}
        {...attributes}
        {...listeners}
      >
        <CardContent
          isDragOverlay={!!isDragOverlay}
          showDragHandle={showDragHandle}
        >
          {children}
        </CardContent>
        {showDragHandle && (
          <DragHandle
            isDragOverlay={!!isDragOverlay}
            setActivatorNodeRef={setActivatorNodeRef}
          />
        )}
      </CardPaper>
    )
  }
)

export default Card

const CardPaper = styled(Paper)({
  display: 'flex',
  flexDirection: 'row',
  width: 'calc(100% - 16px)',
  border: `1.5px solid ${colorPalette.monotone[2]}`,
})

const CardContent = styled('div')<{
  isDragOverlay: boolean
  showDragHandle: boolean
}>(props => ({
  width: props.showDragHandle ? 'calc(100% - 24px)' : '100%',
  padding: '8px',
  pointerEvents: props.isDragOverlay ? 'none' : 'auto',
  userSelect: 'none',
}))
